/**
 * Integrytas Group - Data Integrity Watchdog (ADI)
 * Page: ADI.WATCHDOG
 *
 * Register health assertions (SQL that isolates inconsistent rows), preview
 * them, trigger a scan, and review the central health log with snapshots of
 * any invalid state found.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import type { AuthSession } from '../../types/auth';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ADI.WATCHDOG';

registerUdcHost('ADI.WATCHDOG', 'adi_assertion', 'Integrity Assertions');

const CATEGORIES = ['STRUCTURAL', 'LOGICAL', 'ORPHAN', 'PARAMETER'];
const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const SEVERITY_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  CRITICAL: { labelKey: 'adi.severity.critical', label: 'Critical', variant: 'danger' },
  HIGH:     { labelKey: 'adi.severity.high',     label: 'High',     variant: 'warning' },
  MEDIUM:   { labelKey: 'adi.severity.medium',   label: 'Medium',   variant: 'info' },
  LOW:      { labelKey: 'adi.severity.low',      label: 'Low',      variant: 'neutral' },
};

const RESULT_STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  PASS:  { labelKey: 'adi.status.pass',  label: 'Pass',  variant: 'success' },
  FAIL:  { labelKey: 'adi.status.fail',  label: 'Fail',  variant: 'danger' },
  ERROR: { labelKey: 'adi.status.error', label: 'Error', variant: 'warning' },
};

const ASSERTION_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 2 },
  { key: 'name', type: 'text', label: 'Name', col_span: 2 },
  { key: 'category', type: 'select', label: 'Category', col_span: 1 },
  { key: 'severity', type: 'select', label: 'Severity', col_span: 1 },
  { key: 'wsId', type: 'select', label: 'Workspace', col_span: 1 },
  { key: 'sample_timeout', type: 'custom', label: 'Sample / Timeout(ms)', col_span: 1 },
];
const ASSERTION_CORE_LAYOUT = toUldCoreLayout(ASSERTION_FIELD_DEFS);

const ASSERT_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',     defaultWidth: 220 },
  { key: 'name',          header: 'Name',     defaultWidth: 200 },
  { key: 'category',      header: 'Category', defaultWidth: 130 },
  { key: 'severity',      header: 'Severity', defaultWidth: 110 },
  { key: 'is_active',     header: 'Active',   defaultWidth: 80  },
];

interface AssertRow { adi_assertion_id: number; internal_code: string; name: string; category: string; severity: string; check_sql: string | null; view_name: string | null; une_workspace_id: number | null; sample_limit: number; timeout_ms: number; is_active: 'Y' | 'N'; description: string | null }
interface ResultRow { adi_result_id: number; adi_assertion_id: number | null; offending_count: number | null; status: string; snapshot: unknown; error_message: string | null; created_at: string }
interface WsLite { une_workspace_id: number; name: string }

export interface AdiAssertionsPageProps { session: AuthSession }

export function AdiAssertionsPage({ session }: AdiAssertionsPageProps) {
  return (
    <PersonalizationProvider pageCode="ADI.WATCHDOG" session={session}>
      <AdiAssertionsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function AdiAssertionsPageInner({ session }: AdiAssertionsPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<AssertRow[]>([]);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [ws, setWs] = useState<WsLite[]>([]);
  const [editing, setEditing] = useState<AssertRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ category: string[]; severity: string[]; active: string[] }>(
    { category: [], severity: [], active: ['Y'] },
  );
  const detail = useExpandedRows<number>();

  // §17.B13 related children (expand/contract) — an assertion's recent scan results,
  // labelled by localized status (never a raw id; the timestamp rides along in `title`).
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'results',
      table: 'adi_result', pkColumn: 'adi_result_id',
      title: t('adi.related.results', 'Recent Results'),
      items: results
        .filter(r => r.adi_assertion_id === id)
        .map(r => ({
          id: r.adi_result_id,
          label: RESULT_STATUS_META[r.status] ? t(RESULT_STATUS_META[r.status].labelKey, RESULT_STATUS_META[r.status].label) : r.status,
          title: new Date(r.created_at).toLocaleString(),
        })),
    },
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [a, r, w] = await Promise.all([
        apiDb.from('adi_assertion').select('adi_assertion_id, internal_code, name, category, severity, check_sql, view_name, une_workspace_id, sample_limit, timeout_ms, is_active, description').order('severity').order('internal_code'),
        apiDb.from('adi_result').select('adi_result_id, adi_assertion_id, offending_count, status, snapshot, error_message, created_at').order('created_at', { ascending: false }).limit(50),
        apiDb.from('une_workspace').select('une_workspace_id, name').eq('is_active', 'Y').order('name'),
      ]);
      if (a.error) throw a.error;
      setRows((a.data ?? []) as AssertRow[]); setResults((r.data ?? []) as ResultRow[]); setWs((w.data ?? []) as WsLite[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('adi_assertion', 'ADI.WATCHDOG', { pkColumn: 'adi_assertion_id', rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      category: uniq(rows.map(r => r.category)).map(v => ({ value: v, label: v })),
      severity: uniq(rows.map(r => r.severity)).map(v => ({ value: v, label: v })),
      active:   [{ value: 'Y', label: t('common.yes', 'Yes') }, { value: 'N', label: t('common.no', 'No') }],
    };
  }, [rows, t]);

  const facetsActive = facets.category.length + facets.severity.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ category: [], severity: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesUdc = (a: AssertRow) => udcGrid.matchRow(a as unknown as Record<string, unknown>);
    // OR within a facet, AND across facets.
    const matchesFacets = (a: AssertRow) =>
      (facets.category.length === 0 || facets.category.includes(a.category)) &&
      (facets.severity.length === 0 || facets.severity.includes(a.severity)) &&
      (facets.active.length   === 0 || facets.active.includes(a.is_active));
    const base = !q ? rows : rows.filter(a =>
      a.internal_code.toLowerCase().includes(q) || (a.name ?? '').toLowerCase().includes(q) || a.category.toLowerCase().includes(q) || a.severity.toLowerCase().includes(q));
    return base.filter(matchesUdc).filter(matchesFacets);
  }, [rows, filter, facets, udcGrid]);

  const runAll = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('adi_run_manual');
      if (e) throw e;
      const res = data as { ok: boolean; total?: number; failed?: number; error_code?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'ADI.RUN_FAILED');
      setNotice(t('adi.scan_complete', 'Scan complete: {{total}} run, {{failed}} failed.', { total: res.total, failed: res.failed }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const codeFor = (id: number | null) => rows.find(r => r.adi_assertion_id === id)?.internal_code ?? '?';

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('adi.title', 'Integrity Watchdog')}
          description={t('adi.list.description', 'Health assertions that isolate inconsistent rows, plus the results of each scan.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('adi.refresh', 'Refresh')}
              </button>
              <button type="button" onClick={() => void runAll()} disabled={busy} className="btn-secondary flex items-center gap-1.5 disabled:opacity-60">
                {t('adi.run_scan', 'Run Scan Now')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('adi.new', 'New Assertion')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('adi.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('adi.filter_placeholder', 'Filter assertions…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('adi.field.category', 'Category')} options={facetOptions.category} selected={facets.category} onChange={v => setFacets(f => ({ ...f, category: v }))} />
            <FacetFilter label={t('adi.field.severity', 'Severity')} options={facetOptions.severity} selected={facets.severity} onChange={v => setFacets(f => ({ ...f, severity: v }))} />
            <FacetFilter label={t('adi.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('adi.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...ASSERT_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={`mb-4 ${GRID_TABLE_CLASS}`}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('adi.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('adi.col.edit', 'Edit')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <Fragment key={a.adi_assertion_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(a.adi_assertion_id)} hasChildren={hasRelated(a.adi_assertion_id)} onToggle={() => detail.toggle(a.adi_assertion_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{a.internal_code}</span>}
                        {col.key === 'name'          && <span className="text-csm-sm">{a.name}</span>}
                        {col.key === 'category'      && <span className="text-csm-xs">{a.category}</span>}
                        {col.key === 'severity'      && (
                          <StatusBadge
                            label={SEVERITY_META[a.severity] ? t(SEVERITY_META[a.severity].labelKey, SEVERITY_META[a.severity].label) : a.severity}
                            variant={SEVERITY_META[a.severity]?.variant ?? 'neutral'}
                          />
                        )}
                        {col.key === 'is_active'     && <YesNoBadge value={a.is_active} yesLabel={t('common.yes', 'Yes')} noLabel={t('common.no', 'No')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(a as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">{canChange && (<button onClick={e => { e.stopPropagation(); setEditing(a); }} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('adi.edit', 'Edit')}</button>)}</td>
                  </tr>
                  {detail.isOpen(a.adi_assertion_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(a.adi_assertion_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('adi.empty', 'No assertions defined')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('adi.new_plus', '+ New Assertion')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>

        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('adi.health_log', 'Health Log (recent results)')}</div>
          <table className="min-w-full border-collapse text-csm-md">
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr><th className="border-b border-csm-border px-2 py-1 text-left">{t('adi.col.when', 'When')}</th><th className="border-b border-csm-border px-2 py-1 text-left">{t('adi.col.assertion', 'Assertion')}</th><th className="border-b border-csm-border px-2 py-1 text-left">{t('adi.col.status', 'Status')}</th><th className="csm-num border-b border-csm-border px-2 py-1 text-left">{t('adi.col.offending', 'Offending')}</th><th className="border-b border-csm-border px-2 py-1 text-right">{t('adi.col.snapshot', 'Snapshot')}</th></tr>
            </thead>
            <tbody>
              {results.map(r => (
                <Fragment key={r.adi_result_id}>
                  <tr className={r.status === 'FAIL' ? 'bg-csm-danger-soft' : r.status === 'ERROR' ? 'bg-amber-50' : ''}>
                    <td className="border-b border-csm-border-soft px-2 py-1 text-csm-2xs text-csm-watermark">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 font-mono text-csm-xs">{codeFor(r.adi_assertion_id)}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 text-csm-xs">
                      <StatusBadge
                        label={RESULT_STATUS_META[r.status] ? t(RESULT_STATUS_META[r.status].labelKey, RESULT_STATUS_META[r.status].label) : r.status}
                        variant={RESULT_STATUS_META[r.status]?.variant ?? 'neutral'}
                      />
                    </td>
                    <td className="csm-num border-b border-csm-border-soft px-2 py-1 text-csm-xs">{r.offending_count ?? '—'}</td>
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">{(r.status === 'FAIL' || r.status === 'ERROR') && <button onClick={() => setOpen(open === r.adi_result_id ? null : r.adi_result_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">{open === r.adi_result_id ? t('adi.hide', 'Hide') : t('adi.view', 'View')}</button>}</td>
                  </tr>
                  {open === r.adi_result_id && (
                    <tr><td colSpan={5} className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1"><pre className="max-h-64 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{r.error_message ?? JSON.stringify(r.snapshot, null, 2)}</pre></td></tr>
                  )}
                </Fragment>
              ))}
              {results.length === 0 && !busy && <tr><td colSpan={5} className="px-2 py-3"><EmptyState variant="no-data" title={t('adi.no_results', 'No results yet — run a scan.')} /></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {(editing || creating) && (
        <AssertionModal session={session} row={editing} ws={ws}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function AssertionModal({ session, row, ws, onClose, onSaved }: { session: AuthSession; row: AssertRow | null; ws: WsLite[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const id = row?.adi_assertion_id;

  const initialValues = {
    code: row?.internal_code ?? '', name: row?.name ?? '', category: row?.category ?? 'LOGICAL', severity: row?.severity ?? 'HIGH',
    sql: row?.check_sql ?? '', view: row?.view_name ?? '', wsId: row?.une_workspace_id ?? null as number | null,
    sample: row?.sample_limit ?? 20, timeout: row?.timeout_ms ?? 30000, active: row ? row.is_active === 'Y' : false,
  };

  const { values: f, setValues: setF, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('adi_assertion', 'ADI.WATCHDOG', { pkColumn: 'adi_assertion_id', pk: row?.adi_assertion_id });
  const assertionCoreLayout = useMemo(() => [...ASSERTION_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const assertionFieldDefs = useMemo(() => [...ASSERTION_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('ADI.ASSERTIONS.MODAL', assertionCoreLayout);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [preview, setPreview] = useState<{ count?: number; sample?: unknown; error?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<typeof initialValues>) => setF({ ...f, ...p });

  useEffect(() => { reset(initialValues); }, [row?.adi_assertion_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !f.code.trim()) throw new Error(t('adi.err.code_required', 'Code is required.'));
      if (!f.sql.trim() && !f.view.trim()) throw new Error(t('adi.err.sql_or_view', 'Provide a check SQL or a view name.'));
      const payload = { name: f.name.trim() || f.code.trim(), category: f.category, severity: f.severity, check_sql: f.sql || null, view_name: f.view || null, une_workspace_id: f.wsId, sample_limit: f.sample, timeout_ms: f.timeout, is_active: f.active ? 'Y' : 'N' };
      if (isCreate) { const { error: e } = await routedInsert(session, 'adi_assertion', { internal_code: f.code.trim().toUpperCase(), ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'adi_assertion', 'adi_assertion_id', id, payload); if (e) throw e; }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (!f.sql.trim()) { setError(t('adi.err.preview_needs_sql', 'Preview needs check SQL.')); return; }
    setBusy(true); setError(null); setPreview(null);
    try {
      const { data, error: e } = await apiDb.rpc('adi_preview', { p_check_sql: f.sql, p_sample_limit: f.sample });
      if (e) throw e;
      const r = data as { ok: boolean; count?: number; sample?: unknown; error?: string };
      if (!r?.ok) { setPreview({ error: r?.error ?? 'preview failed' }); return; }
      setPreview({ count: r.count, sample: r.sample });
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <>
    <Modal
      title={isCreate ? t('adi.modal.create', 'Create Assertion') : t('adi.modal.edit', 'Edit Assertion — {{code}}', { code: row?.internal_code ?? '' })}
      onClose={handleClose}
      width="3xl"
      headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
      footer={
        <>
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('adi.close', 'Close')}</button>
          <button onClick={() => void runPreview()} disabled={busy} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('adi.preview', 'Preview')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('adi.saving', 'Saving…') : t('adi.save', 'Save')}</button>
        </>
      }
    >
      <div className="mb-3">
        <FieldRenderer
          fields={assertionFieldDefs}
          layout={layout}
          columns={4}
          pageCode="ADI.ASSERTIONS.MODAL"
          session={session}
          render={(fld) => {
            if (fld.key === 'code') return (
              <PersonalizedField componentKey="modal" fieldKey="code" label={t('adi.field.code', 'Code')}>
              <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.field.code', 'Code')}</label>
                <input value={f.code} disabled={!isCreate} onChange={e => set({ code: e.target.value.toUpperCase() })}
                  onBlur={() => {
                    if (isCreate && f.code && !/^[A-Z][A-Z0-9_.]*$/.test(f.code)) {
                      setInternalCodeError(t('adi.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                    } else { setInternalCodeError(null); }
                  }}
                  className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                {internalCodeError
                  ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                  : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('adi.code_help', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                }</>
              </PersonalizedField>
            );
            if (fld.key === 'name') return (
              <PersonalizedField componentKey="modal" fieldKey="name" label={t('adi.field.name', 'Name')}>
              <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.field.name', 'Name')}</label>
                <input value={f.name} onChange={e => set({ name: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></>
              </PersonalizedField>
            );
            if (fld.key === 'category') return (
              <PersonalizedField componentKey="modal" fieldKey="category" label={t('adi.field.category', 'Category')}>
              <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.field.category', 'Category')}</label>
                <select value={f.category} onChange={e => set({ category: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></>
              </PersonalizedField>
            );
            if (fld.key === 'severity') return (
              <PersonalizedField componentKey="modal" fieldKey="severity" label={t('adi.field.severity', 'Severity')}>
              <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.field.severity', 'Severity')}</label>
                <select value={f.severity} onChange={e => set({ severity: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}</select></>
              </PersonalizedField>
            );
            if (fld.key === 'wsId') return (
              <PersonalizedField componentKey="modal" fieldKey="wsId" label={t('adi.field.workspace', 'Workspace')}>
                <FilteredModalSingleSelect
                  label={t('adi.field.workspace', 'Workspace')}
                  options={ws.map(w => ({ internal_code: String(w.une_workspace_id), display_text: w.name }))}
                  selectedCode={f.wsId == null ? null : String(f.wsId)}
                  onChange={(code) => set({ wsId: code ? Number(code) : null })}
                  clearLabel={t('adi.all', 'all')}
                />
              </PersonalizedField>
            );
            if (fld.key === 'sample_timeout') return (
              <PersonalizedField componentKey="modal" fieldKey="sample_timeout" label={t('adi.field.sample_timeout', 'Sample / Timeout(ms)')}>
              <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.field.sample_timeout', 'Sample / Timeout(ms)')}</label>
                <div className="flex gap-1"><input type="number" value={f.sample} onChange={e => set({ sample: Number(e.target.value) })} className="h-8 w-1/2 rounded border border-csm-border px-2 text-csm-sm" /><input type="number" value={f.timeout} onChange={e => set({ timeout: Number(e.target.value) })} className="h-8 w-1/2 rounded border border-csm-border px-2 text-csm-sm" /></div></>
              </PersonalizedField>
            );
            if (udcForm.isUdcKey(fld.key)) return (
              <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
                readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
                {udcForm.renderControl(fld.key)}
              </PersonalizedField>
            );
            return null;
          }}
        />
      </div>
      <div className="mb-3"><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.check_sql_label', 'Check SQL (returns offending rows — count > 0 fails)')}</label>
        <textarea rows={5} value={f.sql} onChange={e => set({ sql: e.target.value })} placeholder="SELECT t.* FROM child t LEFT JOIN parent p ON p.id=t.parent_id WHERE p.id IS NULL" className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" /></div>
      <div className="mb-3"><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('adi.view_label', '…or View name')}</label>
        <input value={f.view} onChange={e => set({ view: e.target.value })} placeholder="vw_orphaned_records" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></div>

      <div className="mb-3 flex items-center gap-3">
        <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={f.active} onChange={e => set({ active: e.target.checked })} className="h-4 w-4 accent-gray-700" /> {t('adi.active_label', 'Active (included in scans)')}</label>
      </div>
      {preview && (
        <div className={`rounded border p-2 ${preview.error ? 'border-csm-danger-border bg-csm-danger-soft' : (preview.count ?? 0) > 0 ? 'border-amber-300 bg-amber-50' : 'border-csm-border bg-csm-surface-alt'}`}>
          {preview.error ? <span className="text-csm-xs text-csm-danger">{preview.error}</span>
            : <><div className="text-csm-xs font-semibold text-csm-ink-soft">{t('adi.offending_rows', '{{n}} offending row(s) ', { n: preview.count })}{(preview.count ?? 0) > 0 ? t('adi.would_fail', '— would FAIL') : t('adi.passes', '— passes')}</div>
                <pre className="mt-1 max-h-48 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(preview.sample, null, 2)}</pre></>}
        </div>
      )}
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>

    {showDiscard && (
      <ConfirmModal
        title={t('adi.discard_title', 'Discard changes?')}
        message={t('adi.discard_message', 'You have unsaved changes that will be lost.')}
        confirmLabel={t('adi.discard_confirm', 'Discard')}
        cancelLabel={t('adi.discard_cancel', 'Keep editing')}
        danger={false}
        onConfirm={() => { reset(); onClose(); }}
        onCancel={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}
